// One-step movement reach. Pure. No React, no Three.js. Owns the
// canonical "where can this unit step in one move?" computation.
// GameBoard uses the result to draw the move-target halos during a
// drag AND to validate the drop destination.
//
// ── Sorcery rules baked in (codex "Move and Attack", "Step",
//     "Range of Motion", "Voidwalk", "Airborne", "Burrowing",
//     "Submerge", "Immobile", "Underground", "Underwater", "Void") ──
//
//   • A step = one adjacent location in the SAME region (cardinal),
//     unless a keyword crosses regions.
//   • Airborne on surface. Diagonal cardinal-or-diagonal step.
//   • Burrowing. Surface ↔ underground of the SAME land site
//     counts as one step; underground ↔ underground of an adjacent
//     cell also one step.
//   • Submerge. Symmetric for water sites and underwater.
//   • Burrowing + Submerge together. Single step also allowed
//     between underground and underwater of ADJACENT cells.
//   • Voidwalk. Single step between void and any region of an
//     adjacent cell (the rule explicitly covers void ↔ surface,
//     void ↔ subsurface, and void ↔ adjacent void).
//   • Immobile. Empty set (cannot take any step).
//   • Avatars never enter void / underground / underwater.
//   • Survival on arrival: void requires Voidwalk; underground
//     requires Burrowing; underwater requires Submerge. The reach
//     set is filtered to exclude any landing the unit can't survive.
//
// Locations are encoded as `${col},${row},${region}` where region ∈
// { 'surface', 'underground', 'underwater', 'void' }.
//
// ── Return shape ──
//   reachableLocationsOneStep(...)
//     → Set<'col,row,region'>. Every legal one-step destination.
//   cellsReachable(...)
//     → Set<'col,row'>. Same set with region stripped, for halo
//       rendering (the cell glow regardless of which region the unit
//       lands in).
//   resolveDropRegion(card, fromRegion, toCol, toRow, reachSet, regionsAtCellFn)
//     → 'surface' | 'underground' | 'underwater' | 'void' | null
//       Picks the destination region for a drop. Preserve current
//       region if available, else surface, else any legal landing.

import {
  hasBurrowing as hasBurrowingKw,
  hasSubmerge as hasSubmergeKw,
} from './burrowSubmerge';
import { regionsAtCell } from './regions';

const SURFACE     = 'surface';
const UNDERGROUND = 'underground';
const UNDERWATER  = 'underwater';
const VOID        = 'void';

// ── Keyword presence helpers ─────────────────────────────────────

import { hasKeyword, isCardSilenced } from './keywordQuery';
import { isDisabled } from '../automation/scope';

export function hasAirborne(card)  { return hasKeyword(card, 'airborne'); }
export function hasVoidwalk(card)  { return hasKeyword(card, 'voidwalk'); }
export function hasImmobile(card)  { return hasKeyword(card, 'immobile'); }

/**
 * Effective "Movement +N" bonus. Movement +N is a passive special
 * ability, so a silenced or disabled unit loses it (codex) and moves at
 * its base one-step range. Reversible. The stored `_movementBonus` is
 * untouched, so the bonus returns when the state clears. Every step-
 * budget computation reads `1 + effectiveMovementBonus(card)`.
 */
export function effectiveMovementBonus(card) {
  if (!card) return 0;
  if (isDisabled(card) || isCardSilenced(card)) return 0;
  return card._movementBonus || 0;
}

// ── Survival check ───────────────────────────────────────────────

/**
 * Can `card` exist at a (col, row, region) without immediately
 * dying or being banished?
 *
 *   void. Needs Voidwalk
 *   underground. Needs Burrowing
 *   underwater. Needs Submerge
 *   surface. Always (Waterbound / Landbound disables exist
 *                 but are out of scope for movement legality.
 *                 they only disable the unit, not block placement)
 *
 * Avatars are special-cased: they never enter void / subsurface.
 */
export function canSurviveAt(card, fullCard, region) {
  if (!card || !region) return false;
  const type = fullCard?.type || card.type;
  if (type === 'Avatar') return region === SURFACE;
  if (region === SURFACE)     return true;
  if (region === VOID)        return hasVoidwalk(card);
  if (region === UNDERGROUND) return hasBurrowingKw(card);
  if (region === UNDERWATER)  return hasSubmergeKw(card);
  return false;
}

// ── Region availability at a cell ────────────────────────────────

// Wrapper around regionsAtCell that's aware of "void." An empty
// cell exposes a void region per the codex Void / Region rules.
function regionsAt(meshes, cellAt, col, row, getFullCard) {
  return regionsAtCell(meshes, cellAt, col, row, getFullCard);
}

// ── One-step reach ───────────────────────────────────────────────

/**
 * Compute every (col, row, region) reachable in one step from
 * `from = { col, row, region }`. Returns Set of string keys.
 *
 * opts.hazardLandings. Codex "Range of Motion" Example 4 (Bottomless
 * Pit) and the Voidwalk Hounds-of-Ondaros example: occupancy hazards
 * are SURVIVAL rules, not movement blockers. A minion may take a legal
 * step into a location it can't survive in (e.g. Voidwalk into the
 * subsurface of an adjacent site without Burrowing/Submerge). It
 * resolves (dies / is banished) on arrival. With the flag on, such
 * landings stay in the reach set; avatars remain hard-blocked ("may
 * NEVER enter." For an avatar the edge itself doesn't exist).
 */
export function reachableLocationsOneStep(card, fullCard, from, meshes, cellAt, totalCols, totalRows, getFullCard, wallBlocker = null, opts = {}) {
  const out = new Set();
  if (!card || !from) return out;
  if (hasImmobile(card)) return out;
  // Codex Disabled: a disabled minion "cannot take steps itself" (though it may
  // still be moved by force). Its own range of motion is empty. Identical to
  // Immobile for the purposes of a self-driven move.
  if (isDisabled(card)) return out;
  const type = fullCard?.type || card.type;

  const { col, row, region } = from;
  const airborneSurface = region === SURFACE && hasAirborne(card);
  const dual = hasBurrowingKw(card) && hasSubmergeKw(card);
  // Wall blockers operate on surface steps only. Walls sit on edges
  // between cell surfaces. Subsurface units pass beneath the wall, so
  // the blocker is consulted only when the move is a surface→surface
  // adjacent step.
  const blocksSurfaceEdge = (nc, nr) =>
    typeof wallBlocker === 'function'
      ? !!wallBlocker(card, { col, row }, { col: nc, row: nr })
      : false;

  // 1. Same-cell same-site region transitions (one step).
  const hereRegions = regionsAt(meshes, cellAt, col, row, getFullCard);
  if (region === SURFACE && hasBurrowingKw(card) && hereRegions.has(UNDERGROUND)) {
    out.add(`${col},${row},${UNDERGROUND}`);
  }
  if (region === SURFACE && hasSubmergeKw(card) && hereRegions.has(UNDERWATER)) {
    out.add(`${col},${row},${UNDERWATER}`);
  }
  if (region === UNDERGROUND && hereRegions.has(SURFACE)) {
    // Surface from underground: one step. No keyword required (Surface
    // verb is universal).
    out.add(`${col},${row},${SURFACE}`);
  }
  if (region === UNDERWATER && hereRegions.has(SURFACE)) {
    out.add(`${col},${row},${SURFACE}`);
  }

  // 2. Adjacent-cell deltas. Diagonals only if Airborne-on-surface.
  const deltas = airborneSurface
    ? [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]
    : [[1, 0], [-1, 0], [0, 1], [0, -1]];

  for (const [dc, dr] of deltas) {
    const nc = col + dc;
    const nr = row + dr;
    if (nc < 0 || nr < 0 || nc >= totalCols || nr >= totalRows) continue;
    const nRegions = regionsAt(meshes, cellAt, nc, nr, getFullCard);
    const diagonal = dc !== 0 && dr !== 0;

    // Surface → adjacent surface (cardinal always, diagonal for Airborne).
    // Walls on the edge between (col,row) and (nc,nr) may block this
    // step. Wall of Air, Wall of Ice, etc. Diagonal steps have no
    // edge (corners aren't walls in Sorcery), so they're never blocked.
    if (region === SURFACE && nRegions.has(SURFACE)) {
      if (diagonal || !blocksSurfaceEdge(nc, nr)) {
        out.add(`${nc},${nr},${SURFACE}`);
      }
    }
    // Adjacent same-region step (cardinal only. Diagonals not allowed
    // for non-Airborne, and Airborne only applies on surface).
    if (!diagonal) {
      if (region === UNDERGROUND && hasBurrowingKw(card) && nRegions.has(UNDERGROUND)) {
        out.add(`${nc},${nr},${UNDERGROUND}`);
      }
      if (region === UNDERWATER && hasSubmergeKw(card) && nRegions.has(UNDERWATER)) {
        out.add(`${nc},${nr},${UNDERWATER}`);
      }
      // Dual-keyword cross-region between adjacent cells.
      if (dual) {
        if (region === UNDERGROUND && nRegions.has(UNDERWATER)) {
          out.add(`${nc},${nr},${UNDERWATER}`);
        }
        if (region === UNDERWATER && nRegions.has(UNDERGROUND)) {
          out.add(`${nc},${nr},${UNDERGROUND}`);
        }
      }
      // Voidwalk. Void ↔ any region of adjacent cell.
      if (hasVoidwalk(card)) {
        if (region === VOID) {
          // Void → any region of adjacent cell (rule: "between adjacent
          // void locations" OR "between the surface of a site and the
          // void" OR "the subsurface and the void").
          for (const r of nRegions) out.add(`${nc},${nr},${r}`);
        } else if (nRegions.has(VOID)) {
          // Any region of current cell → adjacent void.
          out.add(`${nc},${nr},${VOID}`);
        }
      }
    }
  }

  // 3. Filter destinations the unit can't survive AND skip the
  // starting location (no-op move).
  const fromKey = `${col},${row},${region}`;
  const filtered = new Set();
  const avatar = type === 'Avatar';
  for (const key of out) {
    if (key === fromKey) continue;
    const [c, r, regKey] = key.split(',');
    if (hasKeyword(card, 'move_forward_only') && (Number(c) !== col || Number(r) !== row + (card.rotated ? 1 : -1))) continue;
    if (!canSurviveAt(card, fullCard, regKey)) {
      // Avatars never enter void / subsurface. No such edge exists for
      // them regardless of hazardLandings. Minions may opt into the
      // lethal landing when the caller resolves the consequence.
      if (avatar || !opts.hazardLandings) continue;
    }
    filtered.add(`${c},${r},${regKey}`);
  }
  return filtered;
}

/**
 * Multi-step reach via BFS. Equivalent to running
 * `reachableLocationsOneStep` from each frontier cell `steps` times,
 * unioning the results. Step budget = 1 + Movement+N bonus
 * (e.g. Phantom Steed with `_movementBonus: 2` → 3 total steps).
 *
 * Each iteration uses the SAME card (so keyword-driven region
 * transitions and Airborne diagonals still apply at every step).
 * The starting location is excluded from the result (no-op moves
 * aren't a reach option).
 *
 * @param steps total step budget; 1 yields the same shape as the
 *              one-step function modulo the starting-location filter.
 */
export function reachableLocationsInSteps(card, fullCard, from, steps, meshes, cellAt, totalCols, totalRows, getFullCard, wallBlocker = null, opts = {}) {
  const out = new Set();
  if (!card || !from) return out;
  if (hasImmobile(card)) return out;
  // Disabled units can't take their own steps (codex Disabled). No reach.
  if (isDisabled(card)) return out;
  if (steps <= 0) return out;
  const fromKey = `${from.col},${from.row},${from.region}`;

  let frontier = new Set([fromKey]);
  for (let s = 0; s < steps; s++) {
    const next = new Set();
    for (const loc of frontier) {
      const [colStr, rowStr, region] = loc.split(',');
      const stepReach = reachableLocationsOneStep(
        card, fullCard,
        { col: parseInt(colStr, 10), row: parseInt(rowStr, 10), region },
        meshes, cellAt, totalCols, totalRows, getFullCard, wallBlocker, opts,
      );
      for (const dest of stepReach) {
        if (dest === fromKey) continue;
        if (out.has(dest)) continue;
        out.add(dest);
        // A hazard landing (opts.hazardLandings) is TERMINAL: the minion
        // dies / is banished the moment it occupies the location, so the
        // path cannot continue from there.
        if (canSurviveAt(card, fullCard, dest.split(',')[2])) next.add(dest);
      }
    }
    frontier = next;
    if (frontier.size === 0) break;
  }
  return out;
}

/** Strip region from a reach set → unique cell keys for halo render. */
export function cellsReachable(reachSet) {
  const out = new Set();
  for (const key of reachSet) {
    const [c, r] = key.split(',');
    out.add(`${c},${r}`);
  }
  return out;
}

/**
 * Pick the destination region for a drop on cell (toCol, toRow).
 * Preserve `fromRegion` if the reach set offers it at this cell;
 * else fall back to surface; else any other legal region in the
 * reach. Returns null when no legal landing exists at this cell.
 */
export function resolveDropRegion(fromRegion, toCol, toRow, reachSet) {
  const prefer = [fromRegion, SURFACE, UNDERGROUND, UNDERWATER, VOID];
  for (const region of prefer) {
    if (!region) continue;
    if (reachSet.has(`${toCol},${toRow},${region}`)) return region;
  }
  return null;
}

// Convenience: current region of a card at a known cell. Derives
// from card.subsurface (null → surface) and the cell's available
// regions. If the card is on a void cell (no surface), region is
// 'void' regardless of subsurface flag.
export function currentRegion(card, atCell, meshes, cellAt, getFullCard) {
  if (!card || !atCell) return null;
  const regions = regionsAt(meshes, cellAt, atCell.col, atCell.row, getFullCard);
  if (card.subsurface === UNDERGROUND && regions.has(UNDERGROUND)) return UNDERGROUND;
  if (card.subsurface === UNDERWATER && regions.has(UNDERWATER))  return UNDERWATER;
  if (regions.has(SURFACE)) return SURFACE;
  // No site at the cell → void.
  return VOID;
}
